import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import {
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  X,
  Info,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
} from 'lucide-react';
import { adminApi, eventsApi } from '../../services/api';
import type { AITraceDetailResponse, AITraceItem, Event, AppUser } from '../../types';

type Filters = {
  traceId?: string;
  parentTraceId?: string;
  sessionId?: string;
  messageId?: string;
  documentId?: string;
  eventId?: string;
  clientId?: string;
  operation?: string;
  success?: 'any' | 'true' | 'false';
  provider?: string;
  model?: string;
  userId?: string;
  ragSessionId?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
};

function prettyJson(value: any): string {
  if (value === undefined) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

const inputCls =
  'px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full';

const labelCls = 'block text-sm font-medium text-gray-700 mb-1';

function FieldGroup({ label, tooltip, children }: { label: string; tooltip?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col min-w-[220px]">
      <label className={labelCls}>
        {label}
        {tooltip && (
          <span
            className="ml-1 text-gray-400 cursor-help text-xs border border-gray-300 rounded-full px-1"
            title={tooltip}
          >
            ?
          </span>
        )}
      </label>
      {children}
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="ml-1 text-gray-400 hover:text-gray-600"
      title="Copy"
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
    </button>
  );
}

function CollapsibleSection({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-md mb-2">
      <button
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <span>{title}</span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && <div className="px-4 pb-4 pt-2">{children}</div>}
    </div>
  );
}

const OPERATION_OPTIONS = [
  'chat_request',
  'chat_request_stream',
  'chat_llm',
  'chat_llm_stream',
  'intent_classification',
  'rag_chat_request',
  'rag_chat_llm',
  'rag_query_embedding',
  'rag_vector_search',
  'doc_processing_request',
  'doc_reprocessing_request',
  'doc_processing_llm',
  'rag_ingest',
];

const PAGE_SIZE_OPTIONS = [20, 50, 100, 200];

const AITraces: React.FC = () => {
  const [filters, setFilters] = useState<Filters>({ success: 'any' });
  const [loading, setLoading] = useState(false);
  const [traces, setTraces] = useState<AITraceItem[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selected, setSelected] = useState<AITraceDetailResponse | null>(null);
  const [activeTab, setActiveTab] = useState('prompt');

  const [infoOpen, setInfoOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [clients, setClients] = useState<AppUser[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);

  const fetchClients = async () => {
    setClientsLoading(true);
    try {
      const resp = await adminApi.getAllUsers({ role: 'client', limit: 1000, isActive: true });
      setClients(resp.data?.data?.users || []);
    } catch {
      setClients([]);
    } finally {
      setClientsLoading(false);
    }
  };

  const fetchEvents = async (clientId?: string) => {
    setEventsLoading(true);
    try {
      const limitPerPage = 100; // backend validation caps /events limit to 100
      let pageNo = 1;
      let pages = 1;
      const allEvents: Event[] = [];

      do {
        const params: any = { page: pageNo, limit: limitPerPage };
        if (clientId) params.clientId = clientId;

        const resp = await eventsApi.getAll(params);
        const batch = resp.data?.data || [];
        allEvents.push(...batch);

        pages = resp.data?.pagination?.pages || 1;
        pageNo += 1;
      } while (pageNo <= pages);

      const list = allEvents;
      setEvents(list);

      setFilters((prev) => {
        if (!prev.eventId) return prev;
        const stillExists = list.some((ev: Event) => ev._id === prev.eventId);
        return stillExists ? prev : { ...prev, eventId: undefined };
      });
    } catch {
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  const fetchTraces = async (nextPage = page, nextLimit = limit, overrideFilters?: Filters) => {
    const f = overrideFilters !== undefined ? overrideFilters : filters;
    setLoading(true);
    try {
      const from = f.createdAtFrom ? dayjs(f.createdAtFrom).toISOString() : undefined;
      const to = f.createdAtTo ? dayjs(f.createdAtTo).toISOString() : undefined;

      const resp = await adminApi.listAITraces({
        page: nextPage,
        limit: nextLimit,
        traceId: f.traceId || undefined,
        parentTraceId: f.parentTraceId || undefined,
        sessionId: f.sessionId || undefined,
        messageId: f.messageId || undefined,
        documentId: f.documentId || undefined,
        eventId: f.eventId || undefined,
        clientId: f.clientId || undefined,
        operation: f.operation || undefined,
        success: f.success === 'any' ? undefined : f.success === 'true',
        provider: f.provider || undefined,
        model: f.model || undefined,
        userId: f.userId || undefined,
        ragSessionId: f.ragSessionId || undefined,
        from,
        to,
      });

      const data = resp.data.data;
      setTraces(data?.traces || []);
      setTotal(data?.pagination?.total || 0);
      setPage(data?.pagination?.page || nextPage);
      setLimit(data?.pagination?.limit || nextLimit);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to fetch AI traces');
    } finally {
      setLoading(false);
    }
  };

  const openTrace = async (traceId: string) => {
    setModalOpen(true);
    setDetailLoading(true);
    setSelected(null);
    setActiveTab('prompt');
    try {
      const resp = await adminApi.getAITrace(traceId);
      setSelected(resp.data.data || null);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to fetch trace detail');
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchTraces(1, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchClients();
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setAndSearch = (patch: Partial<Filters>) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    fetchTraces(1, limit, next);
  };

  const resetAndSearch = () => {
    const next: Filters = { success: 'any' };
    setFilters(next);
    fetchTraces(1, limit, next);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchTraces(1, limit);
  };

  const totalPages = Math.ceil(total / limit);

  const tabs = selected
    ? [
        { key: 'prompt', label: `Prompt (${selected.partsMeta.counts.prompt || 0} parts)`, content: selected.payloads.prompt || '' },
        { key: 'response', label: `Response (${selected.partsMeta.counts.response || 0} parts)`, content: selected.payloads.response || '' },
        { key: 'request', label: `Request JSON (${selected.partsMeta.counts.request_json || 0} parts)`, content: selected.payloads.requestJson || '' },
        { key: 'raw', label: `Raw response JSON (${selected.partsMeta.counts.raw_response_json || 0} parts)`, content: selected.payloads.rawResponseJson || '' },
        { key: 'meta', label: 'Metadata', content: prettyJson({ trace: selected.trace }) },
      ]
    : [];

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* Main Card */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        {/* Card header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">AI Traces</h2>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
              onClick={() => setInfoOpen(true)}
            >
              <Info size={14} />
              What is AI Tracing?
            </button>
            <button
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
              onClick={() => fetchTraces(1, limit)}
            >
              <RefreshCw size={14} />
              Refresh
            </button>
          </div>
        </div>

        {/* Info alert */}
        <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-md p-3 mb-4 text-sm text-blue-800">
          <Info size={16} className="mt-0.5 shrink-0 text-blue-500" />
          <div>
            <span className="font-medium">AI Tracing captures full prompts/responses and timings for debugging &amp; audit.</span>
            <span className="ml-1">
              Traces are retained for a limited time (default 30 days) and can be searched by client, event, session, message, document, or trace ID.{' '}
              <button className="underline text-blue-700 hover:text-blue-900" onClick={() => setInfoOpen(true)}>
                Learn more
              </button>
              .
            </span>
          </div>
        </div>

        {/* Quick search buttons */}
        <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-sm font-semibold text-gray-700">Common searches:</span>
            <button
              className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
              onClick={() => setAndSearch({ success: 'false' })}
            >
              Show failures
            </button>
            <button
              className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
              onClick={() => setAndSearch({ operation: 'chat_request' })}
            >
              Chat requests
            </button>
            <button
              className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
              onClick={() => setAndSearch({ operation: 'chat_request_stream' })}
            >
              Streaming chat
            </button>
            <button
              className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
              onClick={() => setAndSearch({ operation: 'rag_chat_request' })}
            >
              RAG endpoint
            </button>
            <button
              className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
              onClick={() => setAndSearch({ operation: 'doc_processing_request' })}
            >
              Document processing
            </button>
            <button
              className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
              onClick={() => setAndSearch({ operation: 'rag_ingest' })}
            >
              RAG ingestion
            </button>
            <button
              className="px-2 py-1 text-xs border border-red-300 rounded text-red-600 hover:bg-red-50"
              onClick={resetAndSearch}
            >
              Clear filters
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Use <code className="bg-gray-100 px-1 rounded">Client</code> +{' '}
            <code className="bg-gray-100 px-1 rounded">Event</code> filters to scope quickly. Then use{' '}
            <code className="bg-gray-100 px-1 rounded">sessionId</code> (end-user chat),{' '}
            <code className="bg-gray-100 px-1 rounded">documentId</code> (docs/RAG), or{' '}
            <code className="bg-gray-100 px-1 rounded">traceId</code> (exact call). Use{' '}
            <code className="bg-gray-100 px-1 rounded">messageId</code> to match a specific conversation message.
          </p>
        </div>

        {/* Filter form */}
        <form onSubmit={handleSearch}>
          <div className="flex flex-wrap gap-4 mb-3">
            <FieldGroup label="Session" tooltip="Best starting point for end-user chat investigations (Session.sessionId).">
              <input
                className={inputCls}
                placeholder="e.g. 2c2e7e..."
                value={filters.sessionId || ''}
                onChange={(e) => setFilters((f) => ({ ...f, sessionId: e.target.value }))}
              />
            </FieldGroup>

            <FieldGroup label="Message" tooltip="Conversation messageId (links traces to a specific chat message).">
              <input
                className={inputCls}
                placeholder="e.g. 9f7b..."
                value={filters.messageId || ''}
                onChange={(e) => setFilters((f) => ({ ...f, messageId: e.target.value }))}
              />
            </FieldGroup>

            <FieldGroup label="Document" tooltip="DocumentInfo _id used for document processing and RAG ingestion traces.">
              <input
                className={inputCls}
                placeholder="Mongo ObjectId"
                value={filters.documentId || ''}
                onChange={(e) => setFilters((f) => ({ ...f, documentId: e.target.value }))}
              />
            </FieldGroup>

            <FieldGroup label="Client" tooltip="Filter traces to a specific client account.">
              <select
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                value={filters.clientId || ''}
                onChange={(e) => {
                  const nextClientId = e.target.value || undefined;
                  setFilters((f) => ({ ...f, clientId: nextClientId, eventId: undefined }));
                  fetchEvents(nextClientId);
                }}
              >
                <option value="">{clientsLoading ? 'Loading clients...' : 'All clients'}</option>
                {clients.map((client) => (
                  <option key={client._id} value={client._id}>
                    {client.name} ({client.email})
                  </option>
                ))}
              </select>
            </FieldGroup>

            <FieldGroup label="Event" tooltip="Filter traces to a specific event.">
              <select
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                value={filters.eventId || ''}
                onChange={(e) => setFilters((f) => ({ ...f, eventId: e.target.value || undefined }))}
                disabled={eventsLoading}
              >
                <option value="">{eventsLoading ? 'Loading events...' : 'All events'}</option>
                {events.map((event) => (
                  <option key={event._id} value={event._id}>
                    {event.name}
                  </option>
                ))}
              </select>
            </FieldGroup>
          </div>

          <div className="flex flex-wrap gap-4 mb-3">
            <FieldGroup label="Trace ID" tooltip="Exact trace lookup. Use this if you already have a traceId from logs or another trace.">
              <input
                className={inputCls}
                placeholder="UUID"
                value={filters.traceId || ''}
                onChange={(e) => setFilters((f) => ({ ...f, traceId: e.target.value }))}
              />
            </FieldGroup>

            <FieldGroup label="Operation" tooltip="High-level step type (chat, streaming chat, RAG embedding/search, ingestion, etc).">
              <select
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.operation || ''}
                onChange={(e) => setFilters((f) => ({ ...f, operation: e.target.value }))}
              >
                <option value="">Select operation</option>
                {OPERATION_OPTIONS.map((op) => (
                  <option key={op} value={op}>{op}</option>
                ))}
              </select>
            </FieldGroup>

            <FieldGroup label="Success" tooltip="Filter to only failed traces when investigating errors.">
              <select
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.success || 'any'}
                onChange={(e) => setFilters((f) => ({ ...f, success: e.target.value as Filters['success'] }))}
              >
                <option value="any">Any</option>
                <option value="true">OK only</option>
                <option value="false">Failures only</option>
              </select>
            </FieldGroup>

            <FieldGroup label="Created from" tooltip="Optional time window start. Helpful when there are many traces.">
              <input
                type="datetime-local"
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.createdAtFrom || ''}
                onChange={(e) => setFilters((f) => ({ ...f, createdAtFrom: e.target.value }))}
              />
            </FieldGroup>

            <FieldGroup label="Created to" tooltip="Optional time window end.">
              <input
                type="datetime-local"
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.createdAtTo || ''}
                onChange={(e) => setFilters((f) => ({ ...f, createdAtTo: e.target.value }))}
              />
            </FieldGroup>

            <div className="flex flex-col justify-end">
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                >
                  Search
                </button>
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 text-sm rounded-md hover:bg-gray-50"
                  onClick={resetAndSearch}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          {/* Advanced filters */}
          <div className="border border-gray-200 rounded-md mb-4">
            <button
              type="button"
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 text-left"
              onClick={() => setAdvancedOpen((v) => !v)}
            >
              <span>Advanced filters (optional)</span>
              {advancedOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {advancedOpen && (
              <div className="px-4 pb-4 pt-2">
                <div className="flex flex-wrap gap-4">
                  <FieldGroup label="Parent Trace ID" tooltip="Use this to find all child steps belonging to a parent request (or vice versa).">
                    <input
                      className={inputCls}
                      placeholder="UUID"
                      value={filters.parentTraceId || ''}
                      onChange={(e) => setFilters((f) => ({ ...f, parentTraceId: e.target.value }))}
                    />
                  </FieldGroup>

                  <FieldGroup label="Provider" tooltip="Examples: gemini, openai, mongodb_atlas_vector_search.">
                    <input
                      className={inputCls}
                      placeholder="e.g. gemini"
                      value={filters.provider || ''}
                      onChange={(e) => setFilters((f) => ({ ...f, provider: e.target.value }))}
                    />
                  </FieldGroup>

                  <FieldGroup label="Model" tooltip="Examples: gemini-2.5-flash-lite, gpt-4o, vector_index.">
                    <input
                      className={inputCls}
                      placeholder="model name"
                      value={filters.model || ''}
                      onChange={(e) => setFilters((f) => ({ ...f, model: e.target.value }))}
                    />
                  </FieldGroup>

                  <FieldGroup label="User" tooltip="End-user identifier used by the chatbot (not AppUser id).">
                    <input
                      className={inputCls}
                      placeholder="e.g. user123"
                      value={filters.userId || ''}
                      onChange={(e) => setFilters((f) => ({ ...f, userId: e.target.value }))}
                    />
                  </FieldGroup>

                  <FieldGroup label="RAG session" tooltip="Used by RAG ingestion with SSE (createRAGWithEvents).">
                    <input
                      className={inputCls}
                      placeholder="SSE sessionId"
                      value={filters.ragSessionId || ''}
                      onChange={(e) => setFilters((f) => ({ ...f, ragSessionId: e.target.value }))}
                    />
                  </FieldGroup>
                </div>
              </div>
            )}
          </div>
        </form>

        {/* Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-sm text-gray-500">Loading…</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Action</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Created</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Operation</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Provider / Model</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Success</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Session</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Message</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Document</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Trace</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {traces.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-gray-400 text-sm">
                      No traces found.
                    </td>
                  </tr>
                ) : (
                  traces.map((r) => (
                    <tr key={r.traceId} className="hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <button
                          className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
                          onClick={() => openTrace(r.traceId)}
                        >
                          View
                        </button>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                        {dayjs(r.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <code className="bg-gray-100 px-1 rounded text-xs">{r.operation}</code>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-gray-800">{r.provider || '-'}</div>
                        <div className="text-gray-500 text-xs">{r.model || '-'}</div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {r.success ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-green-50 text-green-700 border-green-200">
                            OK
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-red-50 text-red-700 border-red-200">
                            FAIL
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {r.sessionId ? (
                          <code className="bg-gray-100 px-1 rounded text-xs">{r.sessionId}</code>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {r.messageId ? (
                          <code className="bg-gray-100 px-1 rounded text-xs">{r.messageId}</code>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {r.documentId ? (
                          <code className="bg-gray-100 px-1 rounded text-xs">{r.documentId}</code>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          <span className="font-mono text-xs text-gray-700">{r.traceId}</span>
                          <CopyButton text={r.traceId} />
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!loading && (
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <span>Rows per page:</span>
              <select
                className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={limit}
                onChange={(e) => {
                  const ps = Number(e.target.value);
                  setLimit(ps);
                  fetchTraces(1, ps);
                }}
              >
                {PAGE_SIZE_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <span className="text-gray-500">
                {total > 0 ? `${(page - 1) * limit + 1}–${Math.min(page * limit, total)} of ${total}` : '0'}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                disabled={page <= 1}
                onClick={() => fetchTraces(page - 1, limit)}
              >
                <ChevronLeft size={18} />
              </button>
              <span className="px-2 text-gray-700">
                {page} / {totalPages || 1}
              </span>
              <button
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                disabled={page >= totalPages}
                onClick={() => fetchTraces(page + 1, limit)}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Trace detail modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">
                {selected ? `AI Trace ${selected.trace.traceId}` : 'AI Trace'}
              </h3>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={() => setModalOpen(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-5">
              {detailLoading || !selected ? (
                <p className="text-sm text-gray-500">{detailLoading ? 'Loading…' : 'No trace selected'}</p>
              ) : (
                <div className="flex flex-col gap-4">
                  {/* Summary card */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Summary</h4>
                    <div className="flex flex-col gap-1 text-sm">
                      <div>
                        <span className="font-medium">Operation: </span>
                        <code className="bg-gray-100 px-1 rounded text-xs">{selected.trace.operation}</code>
                      </div>
                      <div>
                        <span className="font-medium">Provider: </span>
                        {selected.trace.provider || '-'}{' '}
                        <span className="font-medium">Model: </span>
                        {selected.trace.model || '-'}
                      </div>
                      <div>
                        <span className="font-medium">Success: </span>
                        {selected.trace.success ? 'true' : 'false'}
                      </div>
                      {selected.trace.error ? (
                        <div className="text-red-600">
                          <span className="font-medium">Error: </span>
                          {prettyJson(selected.trace.error)}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {/* Tabs */}
                  <div>
                    <div className="flex gap-1 border-b border-gray-200 mb-3 overflow-x-auto">
                      {tabs.map((tab) => (
                        <button
                          key={tab.key}
                          className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 transition-colors ${
                            activeTab === tab.key
                              ? 'border-blue-600 text-blue-600 font-medium'
                              : 'border-transparent text-gray-500 hover:text-gray-700'
                          }`}
                          onClick={() => setActiveTab(tab.key)}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                    {tabs.map((tab) =>
                      activeTab === tab.key ? (
                        <pre
                          key={tab.key}
                          className="whitespace-pre-wrap break-words text-xs bg-gray-50 border border-gray-200 rounded p-3 max-h-96 overflow-y-auto"
                        >
                          {tab.content}
                        </pre>
                      ) : null
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Info modal */}
      {infoOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">About AI Tracing</h3>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={() => setInfoOpen(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-5 flex flex-col gap-4 text-sm text-gray-700">
              <p>
                <strong>AI Tracing</strong> is an internal admin-only logging feature that persists the exact AI
                interactions used by the system: <code className="bg-gray-100 px-1 rounded">prompt</code>,{' '}
                <code className="bg-gray-100 px-1 rounded">response</code>, model/provider, generation settings,
                timings, and (when available) usage metadata.
              </p>

              <hr className="border-gray-200" />

              <CollapsibleSection title="Quick start (how to use)" defaultOpen>
                <p className="mb-2">
                  Use this page when you need to debug "what did the model actually see?" or "why did it respond this way?".
                </p>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>
                    <strong>Start with the parent trace</strong>:
                    <ul className="list-disc pl-5 mt-1 space-y-0.5">
                      <li>For normal chat: find <code className="bg-gray-100 px-1 rounded">operation=chat_request</code>.</li>
                      <li>For streaming chat: find <code className="bg-gray-100 px-1 rounded">operation=chat_request_stream</code>.</li>
                      <li>For the RAG endpoint (<code className="bg-gray-100 px-1 rounded">/api/chat/rag</code>): find <code className="bg-gray-100 px-1 rounded">operation=rag_chat_request</code>.</li>
                      <li>For document upload processing: find <code className="bg-gray-100 px-1 rounded">operation=doc_processing_request</code>.</li>
                      <li>For RAG ingestion: find <code className="bg-gray-100 px-1 rounded">operation=rag_ingest</code>.</li>
                    </ul>
                  </li>
                  <li>
                    <strong>Open the trace and inspect:</strong>
                    <ul className="list-disc pl-5 mt-1 space-y-0.5">
                      <li><strong>Prompt tab</strong>: the exact prompt text that was sent to the model.</li>
                      <li><strong>Response tab</strong>: the final reconstructed response text.</li>
                      <li><strong>Metadata tab</strong>: timings, usage, operation, IDs, and error info.</li>
                    </ul>
                  </li>
                  <li>
                    <strong>Correlate child traces</strong> using <code className="bg-gray-100 px-1 rounded">parentTraceId</code> in the trace record (embedding/search/LLM steps).
                  </li>
                </ol>
              </CollapsibleSection>

              <CollapsibleSection title="Example workflows (copy/paste style)">
                <h5 className="font-semibold mb-2">1) "User says the bot replied wrong"</h5>
                <ol className="list-decimal pl-5 space-y-1 mb-4">
                  <li>Filter by <code className="bg-gray-100 px-1 rounded">Session</code> (sessionId from conversations page).</li>
                  <li>Optionally set <code className="bg-gray-100 px-1 rounded">Success=Any</code> and <code className="bg-gray-100 px-1 rounded">Operation=chat_request</code>.</li>
                  <li>Open the newest <code className="bg-gray-100 px-1 rounded">chat_request</code> trace and inspect <strong>Metadata</strong> for RAG usage (<code className="bg-gray-100 px-1 rounded">useRAG</code>, chunks used).</li>
                  <li>Find the child <code className="bg-gray-100 px-1 rounded">chat_llm</code> (or <code className="bg-gray-100 px-1 rounded">chat_llm_stream</code>) trace via <code className="bg-gray-100 px-1 rounded">parentTraceId</code>, then open it.</li>
                  <li>
                    In the <strong>Prompt</strong> tab, check:
                    <ul className="list-disc pl-5 mt-1 space-y-0.5">
                      <li>Conversation history (was the model biased by earlier turns?)</li>
                      <li>Document context / RAG chunks (was the retrieved context irrelevant?)</li>
                      <li>Guardrails/behavioral prompts (did policy constrain the answer?)</li>
                    </ul>
                  </li>
                </ol>

                <hr className="border-gray-200 my-3" />

                <h5 className="font-semibold mb-2">2) "Streaming response looked cut off / weird"</h5>
                <ol className="list-decimal pl-5 space-y-1 mb-4">
                  <li>Filter by <code className="bg-gray-100 px-1 rounded">Message</code> (messageId) or <code className="bg-gray-100 px-1 rounded">Session</code>.</li>
                  <li>Set <code className="bg-gray-100 px-1 rounded">Operation=chat_llm_stream</code>.</li>
                  <li>
                    Open the trace and check <strong>Metadata</strong>:
                    <ul className="list-disc pl-5 mt-1 space-y-0.5">
                      <li><code className="bg-gray-100 px-1 rounded">timingMs.ttft</code> (time to first token)</li>
                      <li><code className="bg-gray-100 px-1 rounded">timingMs.apiCallTotal</code> and total duration</li>
                    </ul>
                  </li>
                  <li>Compare <strong>Response</strong> to the user-facing transcript: if the transcript differs, the issue is likely downstream (SSE/client rendering) rather than the model.</li>
                </ol>

                <hr className="border-gray-200 my-3" />

                <h5 className="font-semibold mb-2">3) "RAG answer is irrelevant"</h5>
                <ol className="list-decimal pl-5 space-y-1 mb-4">
                  <li>Filter by <code className="bg-gray-100 px-1 rounded">Session</code> + set <code className="bg-gray-100 px-1 rounded">Operation=rag_vector_search</code>.</li>
                  <li>Open the search trace and inspect returned chunk list (IDs + similarity).</li>
                  <li>Then open the corresponding <code className="bg-gray-100 px-1 rounded">rag_chat_llm</code> / <code className="bg-gray-100 px-1 rounded">chat_llm</code> trace and verify those chunks are included in the <strong>Prompt</strong>.</li>
                  <li>If the chunks are wrong, the issue is retrieval; if chunks are right but answer is wrong, it's generation.</li>
                </ol>

                <hr className="border-gray-200 my-3" />

                <h5 className="font-semibold mb-2">4) "Document processing failed"</h5>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Filter by <code className="bg-gray-100 px-1 rounded">Document</code> (documentId) + set <code className="bg-gray-100 px-1 rounded">Success=Failures only</code>.</li>
                  <li>Open <code className="bg-gray-100 px-1 rounded">doc_processing_llm</code> trace and review error + prompt/response.</li>
                  <li>If parsing failed, check metadata fields like <code className="bg-gray-100 px-1 rounded">parseSucceeded</code> / <code className="bg-gray-100 px-1 rounded">parseError</code> and inspect the raw response text.</li>
                </ol>
              </CollapsibleSection>

              <CollapsibleSection title="What each tab means (Prompt / Response / Request JSON / Raw response JSON)">
                <table className="w-full text-sm border border-gray-200 rounded">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 w-40">Tab</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr>
                      <td className="px-3 py-2 font-medium">Prompt</td>
                      <td className="px-3 py-2">Full text prompt sent to the model (or embedding service). This is the most important artifact for debugging model behavior.</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 font-medium">Response</td>
                      <td className="px-3 py-2">Final reconstructed response text. For streaming calls, this is the concatenation of all tokens as received and buffered by the backend.</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 font-medium">Request JSON</td>
                      <td className="px-3 py-2">Optional: structured JSON we choose to store for some operations. If empty, the app likely did not persist a raw request object for that operation (by design).</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 font-medium">Raw response JSON</td>
                      <td className="px-3 py-2">Optional: the raw provider response stored as JSON (typically for OpenAI fallback). If empty, the provider SDK may not expose a stable raw JSON object, or we intentionally stored only the final text to reduce storage.</td>
                    </tr>
                  </tbody>
                </table>
                <p className="text-xs text-gray-500 mt-2">
                  Large prompts/responses are split into multiple parts behind the scenes and reconstructed here in order.
                </p>
              </CollapsibleSection>

              <CollapsibleSection title="Operation glossary (what each trace means)" defaultOpen>
                <table className="w-full text-sm border border-gray-200 rounded">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 w-52">Operation</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[
                      { op: 'chat_request', desc: 'Parent trace for a standard chat request. Contains end-to-end timing breakdown and RAG summary (chunks used, topK).' },
                      { op: 'chat_request_stream', desc: 'Parent trace for a streaming chat request (SSE). Contains streaming-specific metrics like TTFT (time to first token) and total streaming time.' },
                      { op: 'chat_llm', desc: 'A single LLM generation call for chat (Gemini or OpenAI fallback). Stores full prompt/response, provider/model/config, timings, and usage.' },
                      { op: 'chat_llm_stream', desc: 'A streaming LLM call for chat (Gemini streaming). Stores the full prompt and the final full reconstructed response, plus TTFT and streaming timing.' },
                      { op: 'rag_query_embedding', desc: 'Turns the user query into an embedding vector. Prompt is the query text; response is not stored as a vector (to avoid heavy storage), but metadata includes embedding dimensions and timing.' },
                      { op: 'rag_vector_search', desc: 'MongoDB Atlas vector search step. Stores search parameters (topK, numCandidates) and the list of returned chunks (IDs/index/similarity) in trace metadata.' },
                      { op: 'rag_chat_request', desc: 'Parent trace for the RAG chat endpoint (/api/chat/rag). It links to child traces for embedding, search, and the final LLM call.' },
                      { op: 'rag_chat_llm', desc: 'The final LLM call for RAG endpoint after chunks are retrieved and inserted into the prompt.' },
                      { op: 'doc_processing_request', desc: 'Parent trace for document upload processing. Used to correlate the overall request with the child LLM trace.' },
                      { op: 'doc_reprocessing_request', desc: 'Parent trace for manual document reprocessing. Similar to upload processing, but for an existing document.' },
                      { op: 'doc_processing_llm', desc: 'The LLM call used to extract/structure document info. Stores prompt/response, parse success, keyword/summary stats, and timings.' },
                      { op: 'rag_ingest', desc: 'RAG ingestion pipeline (chunking → embeddings → storage). Stores timing breakdown, retry/timeout counts, embedding dims, and a sampled list of stored DocumentChunk IDs.' },
                    ].map(({ op, desc }) => (
                      <tr key={op}>
                        <td className="px-3 py-2">
                          <code className="bg-gray-100 px-1 rounded text-xs">{op}</code>
                        </td>
                        <td className="px-3 py-2">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CollapsibleSection>

              <CollapsibleSection title="Why prompts/responses show up (and why they can look incomplete)">
                <p className="mb-2">
                  The <strong>Prompt</strong> tab exists because "what the model saw" is often the difference between a
                  good and bad output. The system prompt includes guardrails, behavioral prompts, document context
                  (including RAG chunks), and conversation history.
                </p>
                <h5 className="font-semibold mb-1 mt-3">Common reasons a prompt/response may not show "everything"</h5>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong>Storage splitting</strong>: very large prompts/responses are split into multiple parts. The UI reconstructs them, but if some parts failed to write (rare), the reconstructed text may be truncated.</li>
                  <li><strong>Early failure</strong>: if the request failed before the model returned content, you may see a prompt but an empty response (or only error metadata).</li>
                  <li><strong>Not all raw JSON is persisted</strong>: for some providers/SDKs we store only the final text + high-signal metadata (to reduce storage and avoid fragile SDK internals). That's why the JSON tabs may be empty.</li>
                  <li><strong>RAG ingestion link sampling</strong>: ingestion traces store only a sample of chunk IDs (to avoid huge trace docs). The chunks themselves are in the <code className="bg-gray-100 px-1 rounded">DocumentChunk</code> collection.</li>
                  <li><strong>Embedding vectors are not stored here</strong>: embeddings can be large; we persist only dimensions/timings and link to stored chunks.</li>
                  <li><strong>Secrets are intentionally excluded</strong>: API keys are never persisted in traces.</li>
                </ul>
              </CollapsibleSection>

              <CollapsibleSection title="Retention, privacy, and limits">
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong>Retention</strong>: traces auto-expire via TTL (default 30 days). Configure via <code className="bg-gray-100 px-1 rounded">AI_TRACE_RETENTION_DAYS</code>.</li>
                  <li><strong>Large payloads</strong>: stored in parts to avoid Mongo document limits. Configure via <code className="bg-gray-100 px-1 rounded">AI_TRACE_PART_MAX_CHARS</code>.</li>
                  <li><strong>Admin only</strong>: visible only to <strong>admin role</strong> accounts; client logins cannot access the UI or backend endpoints.</li>
                </ul>
              </CollapsibleSection>

              <p className="text-xs text-gray-500">
                Tip: If you're debugging a user complaint, start from <code className="bg-gray-100 px-1 rounded">sessionId</code> and open the most recent{' '}
                <code className="bg-gray-100 px-1 rounded">chat_request</code> trace. Then inspect the child{' '}
                <code className="bg-gray-100 px-1 rounded">rag_query_embedding</code> /{' '}
                <code className="bg-gray-100 px-1 rounded">rag_vector_search</code> /{' '}
                <code className="bg-gray-100 px-1 rounded">chat_llm</code> traces to see the full chain.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AITraces;
